/**
 * components/landing/home/InteractiveProduct.jsx — Phase 2.1 · signature
 * product showcase.
 *
 * Full-width interactive product tour with a 12-module navigation on
 * the left and a live viewer on the right.  Each module renders inside
 * a shared MockDashboardChrome frame so the whole tour reads as one
 * product family.
 *
 * Layouts
 * ───────
 *   Desktop (lg+): sticky vertical nav + large viewer (2-col grid).
 *   Tablet (md):   horizontal chip strip + viewer stacked below.
 *   Mobile (sm):   horizontally-scrollable chip strip + viewer below.
 *
 * Deep-linking
 * ────────────
 * Reads `#module-<id>` on mount and syncs the URL hash whenever the
 * active module changes.  This lets sales / marketing paste direct
 * links like `kalkiai.trade/#module-ai-mentor`.
 *
 * Motion
 * ──────
 * View transitions use a simple opacity + translateY fade on the
 * key-frame.  Users with prefers-reduced-motion set skip the fade.
 */

import { useEffect, useState, useMemo, useRef } from 'react'
import clsx from 'clsx'
import Link from '@/shims/NextLink'
import {
  LayoutDashboard, Bot, BarChart3, Wand2, LineChart, Wallet, PieChart, ScanLine,
  GraduationCap, CreditCard, TrendingUp, Building2, ArrowRight, Sparkles,
} from 'lucide-react'
import { Container, Eyebrow, SectionHeading, GradientButton, GhostButton } from '../design-system'
import { MockDashboardChrome, GlowCard, useInView } from './_shared'
import { VIEW_REGISTRY } from './interactive/views'

// ─── Module registry (order = display order in the left-nav) ───────────────────────
const MODULES = [
  { id: 'dashboard',      label: 'Dashboard',            desc: 'Platform overview',            icon: LayoutDashboard },
  { id: 'ai-mentor',      label: 'AI Mentor',            desc: 'Grounded RAG assistant',       icon: Bot },
  { id: 'trading',        label: 'Trading Terminal',     desc: 'Charts, orders, positions',    icon: BarChart3 },
  { id: 'strategy',       label: 'Strategy Builder',     desc: 'Visual + code',                icon: Wand2 },
  { id: 'backtest',       label: 'Backtesting',          desc: 'Historical validation',        icon: LineChart },
  { id: 'portfolio',      label: 'Portfolio',            desc: 'Holdings + P&L',               icon: Wallet },
  { id: 'market-viewer',  label: 'Market Viewer',        desc: 'Heatmap + AI insights',        icon: PieChart },
  { id: 'scanner',        label: 'Scanner',              desc: 'Opportunities + patterns',     icon: ScanLine },
  { id: 'academy',        label: 'Trading Academy',      desc: 'Courses + certificates',       icon: GraduationCap },
  { id: 'billing',        label: 'Billing',              desc: 'Subs + invoices',              icon: CreditCard },
  { id: 'analytics',      label: 'Analytics',            desc: 'Growth + engagement',          icon: TrendingUp },
  { id: 'white-label',    label: 'White-Label',          desc: 'Brand + domain',               icon: Building2 },
]

const DEFAULT_MODULE = 'dashboard'

const InteractiveProduct = () => {
  const [active, setActive] = useState(DEFAULT_MODULE)
  const [reduceMotion, setReduceMotion] = useState(false)
  const [transitionKey, setTransitionKey] = useState(0)
  const sectionRef = useRef(null)
  const inView = useInView(sectionRef, { rootMargin: '0px 0px -20% 0px' })

  // Deep-link: read hash on mount, write hash on change.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const initial = (window.location.hash || '').replace(/^#module-/, '')
    if (initial && VIEW_REGISTRY[initial]) setActive(initial)
    // Respect prefers-reduced-motion.
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduceMotion(mq.matches)
    const onChange = () => setReduceMotion(mq.matches)
    mq.addEventListener?.('change', onChange)
    // Listen for hash changes from other in-app links.
    const onHash = () => {
      const id = (window.location.hash || '').replace(/^#module-/, '')
      if (id && VIEW_REGISTRY[id]) setActive(id)
    }
    window.addEventListener('hashchange', onHash)
    return () => {
      mq.removeEventListener?.('change', onChange)
      window.removeEventListener('hashchange', onHash)
    }
  }, [])

  const selectModule = (id) => {
    if (id === active) return
    setActive(id)
    setTransitionKey((k) => k + 1)
    // Update the URL without scrolling the page.
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const url = `${window.location.pathname}${window.location.search}#module-${id}`
      window.history.replaceState(null, '', url)
    }
  }

  const ActiveView = useMemo(() => VIEW_REGISTRY[active] || VIEW_REGISTRY[DEFAULT_MODULE], [active])
  const activeMeta = MODULES.find((m) => m.id === active) || MODULES[0]

  return (
    <section
      ref={sectionRef}
      id="interactive-product"
      className="relative py-24 md:py-40 scroll-mt-24 overflow-hidden"
    >
      {/* Backdrop */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute inset-0 opacity-70" style={{ background: 'radial-gradient(55% 40% at 50% 0%, rgba(255,193,7,0.14), transparent 70%)' }} />
        <div className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(45% 30% at 85% 90%, rgba(16,185,129,0.10), transparent 70%)' }} />
      </div>

      <Container size="wide">
        <SectionHeading
          eyebrow="Interactive Product Experience"
          title={<>Explore the platform, <span className="text-brand-gold">right here</span>.</>}
          subtitle="Click any module to enter it. Every screen you see is the real Kalki Ai Cloud product family — the same UI your customers get on day one."
          align="center"
          className="items-center mx-auto"
        />

        <div className="mt-14 grid gap-4 lg:grid-cols-[300px,1fr] items-start">
          {/* ─── Desktop sticky nav ────────────────────────────────── */}
          <aside
            className="hidden lg:block sticky top-24 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-2 shadow-[0_20px_80px_-30px_rgba(0,0,0,0.9)]"
            aria-label="Product modules"
          >
            <ul role="tablist" className="space-y-1">
              {MODULES.map((m) => {
                const isActive = m.id === active
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      aria-controls={`viewer-${m.id}`}
                      onClick={() => selectModule(m.id)}
                      className={clsx(
                        'w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/60',
                        isActive
                          ? 'bg-brand-gold/[0.08] border border-brand-gold/25 shadow-[0_10px_30px_-10px_rgba(255,193,7,0.35)]'
                          : 'border border-transparent hover:bg-white/[0.03] hover:border-white/5',
                      )}
                    >
                      <div className={clsx(
                        'flex h-9 w-9 flex-none items-center justify-center rounded-lg border transition-colors',
                        isActive ? 'border-brand-gold/40 bg-brand-gold/15 text-brand-gold' : 'border-white/10 bg-white/[0.02] text-zinc-400',
                      )}>
                        <m.icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={clsx('text-[13px] font-semibold truncate', isActive ? 'text-white' : 'text-zinc-200')}>{m.label}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{m.desc}</div>
                      </div>
                      {isActive && <span className="h-1.5 w-1.5 rounded-full bg-brand-gold animate-pulse" />}
                    </button>
                  </li>
                )
              })}
            </ul>
            <div className="mt-3 pt-3 border-t border-white/5 px-2 pb-2">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Ready to launch?</div>
              <div className="flex flex-col gap-2">
                <Link href="/get-started" className="inline-flex items-center justify-center gap-1.5 rounded-full bg-gradient-to-r from-brand-gold to-[#FFE17A] text-black text-[12px] font-semibold py-2 shadow-[0_10px_30px_-10px_rgba(255,193,7,0.5)]">
                  Launch Platform <ArrowRight className="h-3 w-3" />
                </Link>
                <Link href="/contact" className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] text-white text-[12px] font-semibold py-2 hover:bg-white/[0.06]">
                  Schedule Demo
                </Link>
              </div>
            </div>
          </aside>

          {/* ─── Mobile / Tablet chip strip ───────────────────────────────── */}
          <div className="lg:hidden -mx-4 px-4 overflow-x-auto scrollbar-hide" role="tablist" aria-label="Product modules">
            <div className="inline-flex items-center gap-1.5 min-w-max pb-2">
              {MODULES.map((m) => {
                const isActive = m.id === active
                return (
                  <button
                    key={m.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => selectModule(m.id)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] font-medium border transition-all whitespace-nowrap',
                      isActive
                        ? 'border-brand-gold/50 bg-brand-gold/15 text-brand-gold'
                        : 'border-white/10 bg-white/[0.02] text-zinc-300',
                    )}
                  >
                    <m.icon className="h-3.5 w-3.5" />
                    {m.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* ─── Viewer ────────────────────────────────────────────────────── */}
          <div className="lg:min-h-[720px]" role="tabpanel" id={`viewer-${active}`} aria-labelledby={`tab-${active}`}>
            <GlowCard className="lg:min-h-[720px]">
              <MockDashboardChrome innerClass="lg:min-h-[680px]">
                <div
                  key={reduceMotion ? active : transitionKey}
                  className={clsx(
                    'h-full',
                    !reduceMotion && 'animate-brand-fade-up',
                  )}
                  style={!reduceMotion ? { animationDuration: '380ms' } : undefined}
                >
                  <ActiveView />
                </div>
              </MockDashboardChrome>
            </GlowCard>

            {/* Footer strip — CTA + shortcut nav for mobile */}
            <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                <Sparkles className="h-3.5 w-3.5 text-brand-gold" />
                Every module is the real Kalki Ai Cloud product family.
                <span className="hidden sm:inline">·</span>
                <span className="hidden sm:inline font-mono">#module-{active}</span>
              </div>
              <div className="flex items-center gap-2">
                <GhostButton href="/contact" size="sm">Schedule demo</GhostButton>
                <GradientButton href="/get-started" size="sm">Launch platform</GradientButton>
              </div>
            </div>
          </div>
        </div>

        {/* Hidden hover-prefetch hint for search engines / a11y — module list */}
        <div className="sr-only" aria-hidden="false">
          <h3>Explore modules</h3>
          <ul>
            {MODULES.map((m) => (
              <li key={m.id}>{m.label}: {m.desc} — {activeMeta.id === m.id ? 'currently selected' : 'select to view'}</li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  )
}

export default InteractiveProduct
